import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  CircularProgress,
  Typography,
  Divider,
  Popover,
  Alert,
} from "@mui/material";
import { Close, Add, Delete, Circle } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import useLabelsAPIClient from "../../api/labels/hooks/useLabelsAPIClient";
import { dispatch } from "../../redux/store";
import {
  addTaskLabel,
  updateTaskLabel,
  removeTaskLabel,
} from "../../redux/actions/user";
import { setOverlayVisible } from "../../redux/actions/overlay";
import {
  PICKER_COLORS,
  DEFAULT_LABEL_COLOR,
} from "../../constants/pickerColors";
import { getRandomLabelColor } from "../../helpers/colorUtils";

// Yellow and White colors should be disabled for labels
const DISABLED_LABEL_COLORS = ["#ffa600", "#ffffff"];

const MAX_LABELS = 100;

const LabelsDialog = ({
  open,
  onClose,
  selectedLabels,
  onSelectedLabelsChange,
  onLabelRemoved,
}) => {
  const { labels: userLabels } = useSelector((state) => state.user);
  const reduxDispatch = useDispatch();
  const [newLabelText, setNewLabelText] = useState("");
  const [selectedColor, setSelectedColor] = useState(getRandomLabelColor());
  const [loading, setLoading] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [updatingColor, setUpdatingColor] = useState(false);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameText, setEditingNameText] = useState("");
  const [updatingName, setUpdatingName] = useState(false);
  const { addNewLabel, updateLabel, removeLabel } = useLabelsAPIClient();

  const isAtLabelLimit = Object.keys(userLabels || {}).length >= MAX_LABELS;

  const handleLabelToggle = (labelId) => {
    if (selectedLabels.includes(labelId)) {
      onLabelRemoved(labelId);
    } else {
      onSelectedLabelsChange([...selectedLabels, labelId]);
    }
  };

  const handleDeleteLabel = async (labelId, e) => {
    e.stopPropagation();

    const onConfirmCallback = async () => {
      try {
        await removeLabel(labelId);
        dispatch(removeTaskLabel(labelId));
        // Also remove from selected labels if it was selected
        if (selectedLabels.includes(labelId)) {
          onLabelRemoved(labelId);
        }
        reduxDispatch(setOverlayVisible({ visible: false }));
      } catch (error) {
        console.error("Failed to delete label:", error);
        reduxDispatch(setOverlayVisible({ visible: false }));
        alert("Failed to delete label. Please try again.");
      }
    };

    reduxDispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText:
          "Are you sure you want to delete this label? It will be removed from all tasks.",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  const handleColorClick = (event, labelId) => {
    event.stopPropagation();
    setEditingLabelId(labelId);
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
    setEditingLabelId(null);
  };

  const handleColorChange = async (color) => {
    if (editingLabelId === "new") {
      // Setting color for new label
      setSelectedColor(color);
      handleColorPickerClose();
      return;
    }

    if (!editingLabelId) return;

    setUpdatingColor(true);
    try {
      await updateLabel(editingLabelId, { color });
      dispatch(updateTaskLabel(editingLabelId, { color }));
    } catch (error) {
      console.error("Failed to update label color:", error);
    }
    setUpdatingColor(false);
    handleColorPickerClose();
  };

  const handleEditNameClick = (label) => {
    setEditingNameId(label.labelId);
    setEditingNameText(label.name);
  };

  const handleCancelEditName = () => {
    setEditingNameId(null);
    setEditingNameText("");
  };

  const handleSaveNameChange = async (labelId) => {
    if (
      !editingNameText.trim() ||
      editingNameText === userLabels[labelId]?.name
    ) {
      handleCancelEditName();
      return;
    }

    setUpdatingName(true);
    try {
      await updateLabel(labelId, { name: editingNameText });
      dispatch(updateTaskLabel(labelId, { name: editingNameText }));
      handleCancelEditName();
    } catch (error) {
      console.error("Failed to update label name:", error);
    }
    setUpdatingName(false);
  };

  const handleNameKeyPress = (e, labelId) => {
    if (e.key === "Enter") {
      handleSaveNameChange(labelId);
    } else if (e.key === "Escape") {
      handleCancelEditName();
    }
  };

  const handleCreateNewLabel = async () => {
    if (newLabelText.trim()) {
      setLoading(true);
      try {
        const { labelId } = await addNewLabel({
          name: newLabelText,
          color: selectedColor,
        });
        setLoading(false);
        const newLabels = [...selectedLabels, labelId];
        onSelectedLabelsChange(newLabels);

        const newLabel = {
          name: newLabelText,
          labelId: labelId,
          createdAt: labelId,
          color: selectedColor,
        };
        dispatch(addTaskLabel(labelId, newLabel));

        setNewLabelText("");
        setSelectedColor(getRandomLabelColor());
      } catch (error) {
        console.error("Failed to create label:", error);
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading && newLabelText.trim()) {
      handleCreateNewLabel();
    }
  };

  const labelArray = Object.values(userLabels || {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1301, // Above TaskHelper/TaskCreator dialog (MUI default is 1300). Overlay is at 1600 and will correctly appear above this.
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Manage Labels
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: "grey.500",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select labels for this task (click the color or label name to change
            it):
          </Typography>

          {labelArray.length > 0 ? (
            <List
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              {labelArray.map((label) => (
                <ListItem
                  key={label.labelId}
                  disablePadding
                  sx={{ mb: 0 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      size="small"
                      onClick={(e) => handleDeleteLabel(label.labelId, e)}
                      title="Delete label"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleLabelToggle(label.labelId)}
                    role={undefined}
                    dense
                    sx={{ py: 0.5 }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedLabels.includes(label.labelId)}
                      tabIndex={-1}
                      disableRipple
                      sx={{ ml: 1 }}
                    />
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleColorClick(e, label.labelId)}
                        sx={{ p: 0.5 }}
                        title="Click to change color"
                      >
                        <Circle
                          sx={{
                            fontSize: "1rem",
                            color: label.color || DEFAULT_LABEL_COLOR,
                          }}
                        />
                      </IconButton>
                    </ListItemIcon>
                    {editingNameId === label.labelId ? (
                      <TextField
                        autoFocus
                        size="small"
                        value={editingNameText}
                        onChange={(e) =>
                          setEditingNameText(e.target.value.slice(0, 16))
                        }
                        onKeyDown={(e) => handleNameKeyPress(e, label.labelId)}
                        onBlur={() => handleSaveNameChange(label.labelId)}
                        disabled={updatingName}
                        sx={{
                          flex: 1,
                          m: "0 !important",
                          "& .MuiInputBase-root": {
                            m: "0 !important",
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "0.95rem",
                            py: 0.5,
                            m: "0 !important",
                          },
                        }}
                      />
                    ) : (
                      <ListItemText
                        primary={label.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditNameClick(label);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.7,
                          },
                          "& .MuiListItemText-primary": {
                            fontSize: "0.95rem",
                            m: "0 !important",
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No labels yet. Create one to get started.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Create a new label:
          </Typography>
          {isAtLabelLimit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have reached the maximum limit of {MAX_LABELS} labels. Please
              delete some labels before creating new ones.
            </Alert>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton
              onClick={(e) => {
                setColorPickerAnchor(e.currentTarget);
                setEditingLabelId("new");
              }}
              sx={{ p: 0.5 }}
              title="Click to change color"
              disabled={isAtLabelLimit}
            >
              <Circle
                sx={{
                  fontSize: "1.5rem",
                  color: selectedColor,
                }}
              />
            </IconButton>
            <TextField
              fullWidth
              size="small"
              placeholder="Label name (max 16 chars)"
              value={newLabelText}
              onChange={(e) =>
                setNewLabelText(e.target.value.slice(0, 16))
              }
              onKeyDown={handleKeyPress}
              disabled={loading || isAtLabelLimit}
              variant="outlined"
              sx={{ m: 0 }}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewLabel}
            disabled={loading || !newLabelText.trim() || isAtLabelLimit}
            fullWidth
            startIcon={loading ? <CircularProgress size={20} /> : <Add />}
          >
            {loading ? "Creating..." : "Create Label"}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>

      {/* Color Picker Popover for editing labels and new label */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        sx={{ zIndex: 1302 }} // Above LabelsDialog (1301)
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select a color:
          </Typography>
          {updatingColor ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 1,
              }}
            >
              {PICKER_COLORS.filter(
                (color) => !DISABLED_LABEL_COLORS.includes(color.value),
              ).map((color) => {
                // Determine current color based on whether we're editing new or existing label
                const currentColor =
                  editingLabelId === "new"
                    ? selectedColor
                    : userLabels[editingLabelId]?.color || DEFAULT_LABEL_COLOR;
                return (
                  <Box
                    key={color.value}
                    onClick={() => handleColorChange(color.value)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: color.value,
                      cursor: "pointer",
                      border:
                        currentColor === color.value
                          ? "3px solid white"
                          : "2px solid transparent",
                      boxShadow:
                        currentColor === color.value
                          ? `0 0 0 2px ${color.value}`
                          : "none",
                      "&:hover": { transform: "scale(1.1)" },
                      transition: "transform 0.1s",
                    }}
                    title={color.name}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Popover>
    </Dialog>
  );
};

export default LabelsDialog;
